import { getDownloadURL, ref } from 'firebase/storage';
import { isFirebaseConfigured, storage } from '../../firebase/client';

const resolvedUrlCache = new Map();
const pendingResolvers = new Map();

/**
 * Returns true when value is a direct browser-loadable URL.
 * @param {string} value Candidate URL.
 * @returns {boolean}
 */
function isDirectUrl(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

/**
 * Resolves a cover image path into a browser-loadable URL.
 * Supports:
 * - direct https URL
 * - gs:// bucket URL
 * - Firebase Storage object path (e.g. products/id/images/file.png)
 * @param {string} pathOrUrl Storage path or URL.
 * @returns {Promise<string | null>}
 */
export async function resolveStorageUrl(pathOrUrl) {
  const candidate = String(pathOrUrl || '').trim();
  if (!candidate) {
    return null;
  }

  if (isDirectUrl(candidate)) {
    return candidate;
  }

  if (!isFirebaseConfigured || !storage) {
    return null;
  }

  if (resolvedUrlCache.has(candidate)) {
    return resolvedUrlCache.get(candidate);
  }

  if (pendingResolvers.has(candidate)) {
    return pendingResolvers.get(candidate);
  }

  const pending = getDownloadURL(ref(storage, candidate))
    .then((downloadUrl) => {
      resolvedUrlCache.set(candidate, downloadUrl);
      pendingResolvers.delete(candidate);
      return downloadUrl;
    })
    .catch(() => {
      pendingResolvers.delete(candidate);
      return null;
    });

  pendingResolvers.set(candidate, pending);
  return pending;
}
